import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { customerService } from '../../../services/customerService';
import { useLocation } from '../../../contexts/LocationContext';
import { productService } from '../../../services/productService';
import unitService from '../../../services/unitService';
import toast from 'react-hot-toast';
import api from '../../../services/api';

const DEFAULT_UNITS = [
  { id: 1, skrot: 'szt', nazwa: 'sztuka' },
  { id: 2, skrot: 'kg', nazwa: 'kilogram' },
  { id: 3, skrot: 'l', nazwa: 'litr' },
  { id: 4, skrot: 'm', nazwa: 'metr' },
  { id: 5, skrot: 'op', nazwa: 'opakowanie' },
  { id: 6, skrot: 'usl', nazwa: 'usluga' }
];

const INITIAL_FORM = {
  numer_faktury: '',
  data_faktury: new Date().toISOString().split('T')[0],
  data_dostawy: '',
  data_platnosci: '',
  sposob_platnosci: 'przelew',
  waluta: 'PLN',
  status: 'nowa',
  uwagi: '',
  customer_id: null,
  nazwa_dostawcy: '',
  nip_dostawcy: '',
  adres_dostawcy: '',
  telefon_dostawcy: '',
  email_dostawcy: ''
};

export function usePurchaseInvoiceData() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { locationId } = useLocation() || {};
  const isNewMode = !id || id === 'new';

  const [formData, setFormData] = useState(INITIAL_FORM);
  const [products, setProducts] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [showCustomerResults, setShowCustomerResults] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [productSearchIndex, setProductSearchIndex] = useState(null);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [showProductResults, setShowProductResults] = useState(false);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  const [saveNewProducts, setSaveNewProducts] = useState(true);
  const [units, setUnits] = useState(DEFAULT_UNITS);
  const [loading, setLoading] = useState(!isNewMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const productSearchRef = useRef(null);

  // Load units
  useEffect(() => {
    const loadUnits = async () => {
      try {
        const data = await unitService.getActiveUnits();
        if (Array.isArray(data) && data.length > 0) {
          const valid = data.filter(u => u.skrot || u.symbol);
          setUnits(valid.length > 0 ? valid : DEFAULT_UNITS);
        }
      } catch {
        setUnits(DEFAULT_UNITS);
      }
    };
    loadUnits();
  }, []);

  // Load invoice for edit mode
  useEffect(() => {
    if (!isNewMode && id) {
      loadInvoiceDetails();
    }
  }, [id, isNewMode]);

  // Customer search debounce
  useEffect(() => {
    if (customerSearch.length < 2) {
      setCustomerResults([]);
      setShowCustomerResults(false);
      return;
    }
    const tid = setTimeout(() => searchCustomers(), 300);
    return () => clearTimeout(tid);
  }, [customerSearch]);

  // Product search debounce
  useEffect(() => {
    if (productSearchQuery.length < 2) {
      setProductSearchResults([]);
      setShowProductResults(false);
      return;
    }
    const tid = setTimeout(() => searchProductsInDb(), 300);
    return () => clearTimeout(tid);
  }, [productSearchQuery]);

  // Close product results on outside click
  useEffect(() => {
    const handler = (e) => {
      if (productSearchRef.current && !productSearchRef.current.contains(e.target)) {
        setShowProductResults(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadInvoiceDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/purchase-invoices/${id}`);
      const data = response.data;

      if (data.success) {
        const supplierName = data.data.dostawca_nazwa || data.data.nazwa_dostawcy || '';
        // Powiazanie z kartoteka czytamy WYLACZNIE z kontrahent_id.
        // Starej kolumny dostawca_id NIE uzywamy - wskazuje ona na martwa
        // tabele 'dostawcy' i pokazalaby falszywe powiazanie z przypadkowa karta.
        const kontrahentId = data.data.kontrahent_id || data.data.customer_id || null;
        // Backend moze doloczyc karte kontrahenta (nazwa + numer_klienta) - jesli
        // jej nie ma, opisujemy powiazanie snapshotem z faktury.
        const kartaKontrahenta = data.data.kontrahent || null;
        setFormData({
          numer_faktury: data.data.numer_faktury || '',
          data_faktury: data.data.data_faktury || '',
          data_dostawy: data.data.data_dostawy || '',
          data_platnosci: data.data.data_platnosci || '',
          sposob_platnosci: data.data.sposob_platnosci || 'przelew',
          waluta: data.data.waluta || 'PLN',
          status: data.data.status || 'nowa',
          uwagi: data.data.uwagi || '',
          customer_id: kontrahentId,
          nazwa_dostawcy: supplierName,
          nip_dostawcy: data.data.dostawca_nip || data.data.nip_dostawcy || '',
          adres_dostawcy: data.data.dostawca_adres || data.data.adres_dostawcy || '',
          telefon_dostawcy: data.data.dostawca_telefon || data.data.telefon_dostawcy || '',
          email_dostawcy: data.data.dostawca_email || data.data.email_dostawcy || ''
        });

        if (supplierName) {
          setCustomerSearch(supplierName);
        }
        if (kontrahentId) {
          setSelectedCustomer({
            id: kontrahentId,
            nazwa_firmy: kartaKontrahenta?.nazwa_firmy || kartaKontrahenta?.nazwa || supplierName,
            numer_klienta: kartaKontrahenta?.numer_klienta || '',
            nip: kartaKontrahenta?.nip || data.data.dostawca_nip || data.data.nip_dostawcy || '',
            address: data.data.dostawca_adres || data.data.adres_dostawcy || ''
          });
        }

        const items = data.data.items || data.data.products || [];
        if (items.length > 0) {
          setProducts(items.map((item, index) => ({
            id: item.id || Date.now() + index,
            product_id: item.produkt_id || item.product_id || item.mapped_product_id || null,
            nazwa: item.nazwa_produktu || item.nazwa || '',
            ean: item.ean || '',
            kod_produktu: item.kod_produktu || '',
            ilosc: item.ilosc || 1,
            jednostka: item.jednostka || 'szt',
            cena_netto: parseFloat(item.cena_netto || 0).toFixed(2),
            vat: item.stawka_vat || 23,
            cena_brutto: parseFloat(item.wartosc_brutto / item.ilosc || item.cena_brutto || 0).toFixed(2),
            isFromDb: true
          })));
        }

        if (data.data.customer) {
          setSelectedCustomer(data.data.customer);
          setCustomerSearch(data.data.customer.nazwa_firmy || `${data.data.customer.imie} ${data.data.customer.nazwisko}`);
        }
      } else {
        setError(data.message);
      }
    } catch {
      setError('Blad polaczenia z serwerem');
    } finally {
      setLoading(false);
    }
  };

  const searchCustomers = async () => {
    setSearchingCustomers(true);
    try {
      // Kartoteka jest wspolna dla klientow i dostawcow - na fakturze zakupu
      // podpowiadamy WYLACZNIE karty z rola dostawcy.
      const response = await customerService.searchCustomers(customerSearch, 10, 'dostawca');
      let customers = [];
      if (response?.success !== false) {
        customers = response?.data?.customers || response?.data || response?.customers || (Array.isArray(response) ? response : []);
      }
      setCustomerResults(Array.isArray(customers) ? customers : []);
      setShowCustomerResults(true);
    } catch {
      setCustomerResults([]);
    } finally {
      setSearchingCustomers(false);
    }
  };

  const searchProductsInDb = async () => {
    setSearchingProducts(true);
    try {
      const results = await productService.searchProducts({ search: productSearchQuery, limit: 10 });
      const productList = results?.products || results || [];
      setProductSearchResults(Array.isArray(productList) ? productList : []);
      setShowProductResults(true);
    } catch {
      setProductSearchResults([]);
    } finally {
      setSearchingProducts(false);
    }
  };

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    const displayName = customer.nazwa_firmy || customer.name || `${customer.imie || ''} ${customer.nazwisko || ''}`.trim();
    setCustomerSearch(displayName);
    setShowCustomerResults(false);
    const address = customer.address || `${customer.ulica || ''}, ${customer.kod_pocztowy || ''} ${customer.miasto || ''}`.trim().replace(/^,\s*/, '');
    setFormData(prev => ({
      ...prev,
      customer_id: customer.id,
      nazwa_dostawcy: displayName,
      nip_dostawcy: customer.nip || '',
      adres_dostawcy: address,
      telefon_dostawcy: customer.telefon || customer.phone || '',
      email_dostawcy: customer.email || ''
    }));
  };

  /**
   * Odpina fakture od karty kontrahenta, ale ZOSTAWIA dane tekstowe.
   * To nie jest to samo co clearCustomer: snapshot dostawcy na dokumencie
   * musi zostac nietkniety, znika tylko powiazanie z kartoteka.
   */
  const odepnijKontrahenta = () => {
    setSelectedCustomer(null);
    setFormData(prev => ({ ...prev, customer_id: null }));
  };

  const clearCustomer = () => {
    setSelectedCustomer(null);
    setCustomerSearch('');
    setFormData(prev => ({
      ...prev,
      customer_id: null,
      nazwa_dostawcy: '',
      nip_dostawcy: '',
      adres_dostawcy: '',
      telefon_dostawcy: '',
      email_dostawcy: ''
    }));
  };

  const handleSelectProduct = (product, index) => {
    setProducts(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        product_id: product.id,
        nazwa: product.nazwa || product.name,
        ean: product.ean || product.kod_kreskowy || product.barcode,
        jednostka: product.jednostka || 'szt',
        cena_netto: parseFloat(product.cena_zakupu || product.cena_sprzedazy_netto || 0).toFixed(2),
        vat: product.stawka_vat || 23,
        cena_brutto: parseFloat(product.cena_zakupu
          ? product.cena_zakupu * (1 + (product.stawka_vat || 23) / 100)
          : product.cena_sprzedazy_brutto || 0).toFixed(2),
        isFromDb: true
      };
      return updated;
    });
    setProductSearchQuery('');
    setProductSearchResults([]);
    setShowProductResults(false);
    setProductSearchIndex(null);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addProduct = () => {
    const newIndex = products.length;
    setProducts(prev => [...prev, {
      id: Date.now(), product_id: null, nazwa: '', ean: '',
      ilosc: 1, jednostka: 'szt', cena_netto: 0, vat: 23, cena_brutto: 0, isFromDb: false
    }]);
    setProductSearchIndex(newIndex);
    setProductSearchQuery('');
  };

  const updateProduct = (index, field, value) => {
    setProducts(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (['cena_netto', 'vat'].includes(field)) {
        const netto = parseFloat(updated[index].cena_netto) || 0;
        const vatRate = parseFloat(updated[index].vat) || 0;
        updated[index].cena_brutto = (netto * (1 + vatRate / 100)).toFixed(2);
      }
      if (field === 'cena_brutto') {
        const brutto = parseFloat(value) || 0;
        const vatRate = parseFloat(updated[index].vat) || 0;
        updated[index].cena_netto = (brutto / (1 + vatRate / 100)).toFixed(2);
      }
      return updated;
    });
  };

  const removeProduct = (index) => {
    setProducts(prev => prev.filter((_, i) => i !== index));
  };

  const calculateTotal = () =>
    products.reduce((sum, p) => sum + (parseFloat(p.cena_brutto) || 0) * (parseFloat(p.ilosc) || 0), 0).toFixed(2);

  const handleSave = async () => {
    if (saving) return;
    if (!formData.nazwa_dostawcy || formData.nazwa_dostawcy.trim() === '') {
      toast.error('Dostawca jest wymagany. Wybierz lub dodaj dostawce przed zapisem faktury.');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      let updatedProducts = [...products];
      if (saveNewProducts) {
        // BARIERA DANYCH (ta sama zasada co w imporcie): nowy produkt nie moze
        // powstac ze ZGADNIETA stawka VAT. Wczesniej bylo tu
        // `parseInt(product.vat) || 23`, wiec reczna faktura byla obejsciem
        // calego kreatora - suplementy 8% ladowaly na kartach z 23%.
        const czyNowy = (p) => !p.isFromDb && !p.product_id && p.nazwa?.trim();
        const stawka = (p) => (p.vat === 0 || p.vat === '0' ? 0 : parseFloat(p.vat));
        const bezVat = updatedProducts
          .filter(p => czyNowy(p) && !Number.isFinite(stawka(p)))
          .map(p => p.nazwa);
        if (bezVat.length > 0) {
          setSaving(false);
          toast.error(
            `Uzupelnij stawke VAT dla nowych pozycji: ${bezVat.slice(0, 3).join(', ')}` +
            (bezVat.length > 3 ? ` (+${bezVat.length - 3})` : '') +
            '. Bez stawki VAT produkt nie zostanie utworzony.'
          );
          return;
        }

        for (let i = 0; i < updatedProducts.length; i++) {
          const product = updatedProducts[i];
          if (czyNowy(product)) {
            const vat = stawka(product);
            try {
              const cenaNetto = parseFloat(product.cena_netto) || 0;
              const cenaBrutto = parseFloat(product.cena_brutto) || 0;
              const result = await productService.addProduct({
                nazwa: product.nazwa, name: product.nazwa,
                kod_kreskowy: product.ean || '', barcode: product.ean || '', ean: product.ean || '',
                cena_zakupu: cenaNetto,
                cena_sprzedazy: cenaBrutto > 0 ? cenaBrutto * 1.2 : 1.23,
                cena: cenaBrutto > 0 ? cenaBrutto * 1.2 : 1.23,
                stawka_vat: vat,
                jednostka: product.jednostka || 'szt'
              });
              if (result?.data) {
                updatedProducts[i] = { ...product, product_id: result.data.id, isFromDb: true };
              }
            } catch {
              // Blad zakladania produktu nie moze byc niewidoczny - inaczej faktura
              // zapisuje sie z pozycjami niezmapowanymi i nikt o tym nie wie.
              toast.error(`Nie udalo sie utworzyc produktu "${product.nazwa}" — pozycja zostanie niezmapowana`);
            }
          }
        }
      }

      const payload = { ...formData, products: updatedProducts };
      if (isNewMode && locationId) {
        payload.location_id = locationId;
      }
      const response = isNewMode
        ? await api.post('/purchase-invoices', payload)
        : await api.put(`/purchase-invoices/${id}`, payload);

      const data = response.data;
      if (data.success) {
        // Backend moze zalozyc karte automatycznie (poprawny NIP) albo dopasowac
        // istniejaca - odczytujemy faktycznie zapisane powiazanie, zeby stan
        // formularza nie klamal, gdy uzytkownik zostanie na ekranie.
        const zapisanyKontrahentId = data.data?.kontrahent_id ?? data.data?.customer_id ?? null;
        if (zapisanyKontrahentId) {
          setFormData(prev => ({ ...prev, customer_id: zapisanyKontrahentId }));
          setSelectedCustomer(prev => (
            prev && prev.id === zapisanyKontrahentId
              ? prev
              : {
                  id: zapisanyKontrahentId,
                  nazwa_firmy: data.data?.kontrahent?.nazwa_firmy || formData.nazwa_dostawcy,
                  numer_klienta: data.data?.kontrahent?.numer_klienta || '',
                  nip: data.data?.kontrahent?.nip || formData.nip_dostawcy || ''
                }
          ));
        }
        const newId = isNewMode ? data.data?.id : id;
        navigate(`/purchase-invoices/${newId || 'list'}`);
      } else {
        setError(data.message || 'Blad podczas zapisywania');
      }
    } catch {
      setError('Blad polaczenia z serwerem');
    } finally {
      setSaving(false);
    }
  };

  return {
    id, isNewMode, locationId, navigate,
    formData, products,
    customerSearch, setCustomerSearch,
    customerResults, showCustomerResults, setShowCustomerResults,
    selectedCustomer,
    productSearchIndex, setProductSearchIndex,
    productSearchQuery, setProductSearchQuery,
    productSearchResults, showProductResults,
    searchingProducts, searchingCustomers,
    saveNewProducts, setSaveNewProducts,
    units,
    loading, saving, error,
    productSearchRef,
    handleInputChange, handleSelectCustomer, clearCustomer, odepnijKontrahenta,
    handleSelectProduct, addProduct, updateProduct, removeProduct,
    calculateTotal, handleSave
  };
}
